import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, CheckCircle, Clock, XCircle, Camera } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// ── Document number formats (El Salvador) ───────────────────────────────────────
// allow = chars permitted while typing; pattern = full-value validation; hint = i18n key
const ID_FORMATS = {
  DUI:       { maxLength: 10, inputMode: 'numeric', allow: /[0-9-]/, pattern: /^\d{8}-?\d$/,        placeholder: '01234567-8', hint: 'verification.hint_dui' },
  passport:  { maxLength: 12, inputMode: 'text',    allow: /[A-Za-z0-9]/, pattern: /^[A-Za-z0-9]{6,12}$/, placeholder: 'A1234567', hint: 'verification.hint_passport' },
  residency: { maxLength: 20, inputMode: 'text',    allow: /[A-Za-z0-9-]/, pattern: /^[A-Za-z0-9-]{5,20}$/, placeholder: '', hint: 'verification.hint_residency' },
};
const NIT_FORMAT = { maxLength: 17, inputMode: 'numeric', allow: /[0-9-]/, pattern: /^\d{4}-?\d{6}-?\d{3}-?\d$|^\d{1,8}$/, placeholder: '0614-123456-001-2', hint: 'verification.hint_nit' };

// Normalize a DUI to ########-# before submit
const normalizeDui = (v) => {
  const d = (v || '').replace(/\D/g, '');
  return d.length === 9 ? `${d.slice(0, 8)}-${d.slice(8)}` : v;
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status, tier, t }) => {
  if (status === 'approved') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 text-sm px-3 py-1">
        <CheckCircle className="w-3.5 h-3.5 mr-1 inline" />
        {t('verification.approved')} — {t(`provider.tier_${tier || 1}`)}
      </Badge>
    );
  }
  if (status === 'pending_review') {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-sm px-3 py-1">
        <Clock className="w-3.5 h-3.5 mr-1 inline" />
        {t('verification.pending')}
      </Badge>
    );
  }
  if (status === 'rejected') {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 text-sm px-3 py-1">
        <XCircle className="w-3.5 h-3.5 mr-1 inline" />
        {t('verification.rejected')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-sm px-3 py-1">
      {t('verification.unverified')}
    </Badge>
  );
};

// ── Document upload row ───────────────────────────────────────────────────────
const DocUploadRow = ({ docType, label, existingDoc, onUploaded }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      await axios.post(`${API}/providers/documents/upload`, fd);
      toast.success(`${label} uploaded`);
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div>
        <p className="font-medium text-sm">{label}</p>
        {existingDoc && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {existingDoc.filename} ·{' '}
            <span className={
              existingDoc.status === 'approved' ? 'text-green-600' :
              existingDoc.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
            }>
              {existingDoc.status}
            </span>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {existingDoc && <CheckCircle className="w-4 h-4 text-green-500" />}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={handleFile}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          {uploading ? 'Uploading…' : existingDoc ? 'Replace' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ProviderVerificationPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [verifStatus, setVerifStatus] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [portfolioUploading, setPortfolioUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_legal_name: '',
    id_document_type: 'DUI',
    id_document_number: '',
    request_license: false,
    request_business: false,
    trade_license_number: '',
    business_registration_number: '',
    business_name: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const photoInputRef = useRef(null);
  const portfolioInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, profileRes] = await Promise.all([
        axios.get(`${API}/providers/verification/status`),
        axios.get(`${API}/providers/${user?.id}/profile`),
      ]);
      const pp = profileRes.data.provider_profile || {};

      // verification data (incl. uploaded-doc status, masked submission) comes
      // from the authenticated status endpoint — the public profile no longer
      // carries any verification PII.
      setVerifStatus(statusRes.data);
      setPortfolio(pp.portfolio || []);
      setProfilePhoto(profileRes.data.profile_photo || pp.profile_photo || null);

      // Prefill only the provider's own legal name (for a rejected resubmission).
      // Never prefill the ID number — it is only ever returned masked.
      const sub = statusRes.data.submission;
      if (sub) {
        setForm(prev => ({
          ...prev,
          full_legal_name: sub.full_legal_name || '',
          id_document_type: sub.id_document_type || 'DUI',
        }));
      }
    } catch {
      toast.error('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.role !== 'provider') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleProfilePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/providers/profile-photo/upload`, fd);
      setProfilePhoto(res.data.profile_photo);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handlePortfolioPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPortfolioUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/providers/portfolio/upload`, fd);
      toast.success('Photo added to portfolio');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setPortfolioUploading(false);
      if (portfolioInputRef.current) portfolioInputRef.current.value = '';
    }
  };

  const deletePortfolioPhoto = async (index) => {
    if (!window.confirm('Remove this photo from your portfolio?')) return;
    try {
      await axios.delete(`${API}/providers/portfolio/${index}`);
      toast.success('Photo removed');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!findDoc('dui')) {
      toast.error(t('verification.upload_dui_to_submit'));
      return;
    }

    // Format validation by document type
    const errs = {};
    const fmt = ID_FORMATS[form.id_document_type] || ID_FORMATS.DUI;
    if (!fmt.pattern.test(form.id_document_number.trim())) {
      errs.id_document_number = t(fmt.hint);
    }
    if (form.request_business && !NIT_FORMAT.pattern.test(form.business_registration_number.trim())) {
      errs.business_registration_number = t(NIT_FORMAT.hint);
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t('verification.invalid_format'));
      return;
    }

    const payload = {
      ...form,
      id_document_number: form.id_document_type === 'DUI'
        ? normalizeDui(form.id_document_number)
        : form.id_document_number.trim(),
    };

    setSubmitting(true);
    try {
      await axios.post(`${API}/providers/verification/submit`, payload);
      toast.success(t('verification.submission_sent'));
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  const docs = verifStatus?.documents || [];
  const findDoc = (type) => docs.find((d) => d.doc_type === type);
  const hasDui = !!findDoc('dui');
  const status = verifStatus?.verification_status;
  const isLocked = status === 'pending_review' || status === 'approved';
  const submission = verifStatus?.submission;
  const idFmt = ID_FORMATS[form.id_document_type] || ID_FORMATS.DUI;

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl space-y-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>
          {t('verification.title')}
        </h1>

        {/* ── Current status ── */}
        <Card>
          <CardContent className="pt-5 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge
                status={status}
                tier={verifStatus?.verification_tier}
                t={t}
              />
              {verifStatus?.is_licensed && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-sm px-3 py-1">
                  {t('verification.badge_licensed')}
                </Badge>
              )}
              {verifStatus?.is_business && (
                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-sm px-3 py-1">
                  {t('verification.badge_business')}
                </Badge>
              )}
            </div>
            {status === 'rejected' && verifStatus?.verification_rejection_reason && (
              <p className="text-sm text-red-600">
                {verifStatus.verification_rejection_reason}
              </p>
            )}
            {status === 'pending_review' && verifStatus?.verification_submitted_at && (
              <p className="text-xs text-muted-foreground">
                Submitted {new Date(verifStatus.verification_submitted_at).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>

        {/* ── Profile photo ── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('verification.change_photo')}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            {profilePhoto ? (
              <img src={profilePhoto} alt="Profile" className="w-16 h-16 rounded-full object-cover border" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                <Camera className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <div>
              <input
                ref={photoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handleProfilePhoto}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={photoUploading}
                onClick={() => photoInputRef.current?.click()}
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" />
                {photoUploading ? 'Uploading…' : t('verification.change_photo')}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">JPEG · PNG · WEBP · max 5 MB</p>
            </div>
          </CardContent>
        </Card>

        {/* ── Locked: read-only summary of submitted identity ── */}
        {isLocked ? (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">{t('verification.your_information')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t('verification.full_legal_name')}</span>
                <span className="font-medium text-right">{submission?.full_legal_name || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">{t('verification.id_document')}</span>
                <span className="font-medium text-right">
                  {(submission?.id_document_type || '').toUpperCase()} · <span className="font-mono">{submission?.id_document_number_masked || '—'}</span>
                </span>
              </div>
              {submission?.trade_license_number_masked && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{t('verification.trade_license_number')}</span>
                  <span className="font-mono font-medium text-right">{submission.trade_license_number_masked}</span>
                </div>
              )}
              {submission?.business_registration_number_masked && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{t('verification.business_registration_number')}</span>
                  <span className="font-mono font-medium text-right">{submission.business_registration_number_masked}</span>
                </div>
              )}
              {submission?.business_name && (
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{t('verification.business_name')}</span>
                  <span className="font-medium text-right">{submission.business_name}</span>
                </div>
              )}
              {/* Uploaded documents — status only */}
              {docs.length > 0 && (
                <div className="border-t pt-3 space-y-1.5">
                  {docs.map((d) => (
                    <div key={d.doc_type} className="flex justify-between gap-4">
                      <span className="text-muted-foreground capitalize">{d.doc_type?.replace('_', ' ')}</span>
                      <span className={
                        d.status === 'approved' ? 'text-green-600' :
                        d.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
                      }>{d.status}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground pt-1">{t('verification.locked_note')}</p>
            </CardContent>
          </Card>
        ) : (
          /* ── Unlocked: editable verification form ── */
          <form onSubmit={handleSubmit}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">{t('verification.your_information')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label>{t('verification.full_legal_name')}</Label>
                    <Input
                      required
                      value={form.full_legal_name}
                      onChange={e => setForm(p => ({ ...p, full_legal_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>{t('verification.id_type')}</Label>
                    <select
                      className="w-full border rounded-md px-3 py-2 text-sm h-10"
                      value={form.id_document_type}
                      onChange={e => {
                        setForm(p => ({ ...p, id_document_type: e.target.value, id_document_number: '' }));
                        setFieldErrors(fe => ({ ...fe, id_document_number: undefined }));
                      }}
                    >
                      <option value="DUI">DUI</option>
                      <option value="passport">{t('verify.passport')}</option>
                      <option value="residency">{t('verify.residency')}</option>
                    </select>
                  </div>
                  <div>
                    <Label>{t('verification.id_number')}</Label>
                    <Input
                      required
                      inputMode={idFmt.inputMode}
                      maxLength={idFmt.maxLength}
                      placeholder={idFmt.placeholder}
                      value={form.id_document_number}
                      onChange={e => {
                        const filtered = [...e.target.value].filter(c => idFmt.allow.test(c)).join('');
                        setForm(p => ({ ...p, id_document_number: filtered }));
                        setFieldErrors(fe => ({ ...fe, id_document_number: undefined }));
                      }}
                      className={fieldErrors.id_document_number ? 'border-red-500' : ''}
                    />
                    {fieldErrors.id_document_number ? (
                      <p className="text-xs text-red-600 mt-1">{fieldErrors.id_document_number}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">{t(idFmt.hint)}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2 space-y-3 pt-1">
                    <p className="text-xs text-muted-foreground">{t('verification.optional_badges')}</p>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={form.request_license}
                        onChange={e => setForm(p => ({ ...p, request_license: e.target.checked }))}
                      />
                      <span>{t('verification.tier3_desc')}</span>
                    </label>
                    {form.request_license && (
                      <div className="pl-6">
                        <Label>{t('verification.trade_license_number')}</Label>
                        <Input
                          required
                          value={form.trade_license_number}
                          onChange={e => setForm(p => ({ ...p, trade_license_number: e.target.value }))}
                        />
                      </div>
                    )}
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={form.request_business}
                        onChange={e => setForm(p => ({ ...p, request_business: e.target.checked }))}
                      />
                      <span>{t('verification.tier4_desc')}</span>
                    </label>
                    {form.request_business && (
                      <div className="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label>{t('verification.business_registration_number')}</Label>
                          <Input
                            required
                            inputMode={NIT_FORMAT.inputMode}
                            maxLength={NIT_FORMAT.maxLength}
                            placeholder={NIT_FORMAT.placeholder}
                            value={form.business_registration_number}
                            onChange={e => {
                              const filtered = [...e.target.value].filter(c => NIT_FORMAT.allow.test(c)).join('');
                              setForm(p => ({ ...p, business_registration_number: filtered }));
                              setFieldErrors(fe => ({ ...fe, business_registration_number: undefined }));
                            }}
                            className={fieldErrors.business_registration_number ? 'border-red-500' : ''}
                          />
                          {fieldErrors.business_registration_number ? (
                            <p className="text-xs text-red-600 mt-1">{fieldErrors.business_registration_number}</p>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1">{t(NIT_FORMAT.hint)}</p>
                          )}
                        </div>
                        <div>
                          <Label>{t('verification.business_name')}</Label>
                          <Input
                            required
                            value={form.business_name}
                            onChange={e => setForm(p => ({ ...p, business_name: e.target.value }))}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Document uploads */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">{t('verification.supporting_documents')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <DocUploadRow docType="dui" label={t('verification.upload_dui')} existingDoc={findDoc('dui')} onUploaded={fetchData} />
                <DocUploadRow docType="license" label={t('verification.upload_license')} existingDoc={findDoc('license')} onUploaded={fetchData} />
                <DocUploadRow docType="background_consent" label={t('verification.upload_consent')} existingDoc={findDoc('background_consent')} onUploaded={fetchData} />
                <p className="text-xs text-muted-foreground pt-1">PDF · JPEG · PNG · max 10 MB</p>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Button
                type="submit"
                disabled={submitting || !hasDui || !form.full_legal_name || !form.id_document_number}
                className="w-full"
                style={hasDui && form.full_legal_name && form.id_document_number
                  ? { background: 'hsl(120 100% 30%)', color: 'white' }
                  : {}}
              >
                {submitting ? 'Submitting…' : t('verification.submit_review')}
              </Button>
              {!hasDui && (
                <p className="text-xs text-muted-foreground text-center">
                  {t('verification.upload_dui_to_submit')}
                </p>
              )}
            </div>
          </form>
        )}

        {/* ── Portfolio ── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">{t('verification.portfolio')}</CardTitle>
            <div>
              <input
                ref={portfolioInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handlePortfolioPhoto}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={portfolioUploading || portfolio.length >= 12}
                onClick={() => portfolioInputRef.current?.click()}
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {portfolioUploading ? 'Uploading…' : t('verification.add_photo')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {portfolio.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No portfolio photos yet
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {portfolio.map((photo, idx) => (
                  <div key={idx} className="relative group aspect-square">
                    <img
                      src={photo.url}
                      alt={`Portfolio ${idx + 1}`}
                      className="w-full h-full object-cover rounded-lg border"
                    />
                    <button
                      onClick={() => deletePortfolioPhoto(idx)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              {portfolio.length}/12 photos · JPEG · PNG · WEBP · max 5 MB
            </p>
          </CardContent>
        </Card>
      </div>
  );
};

export default ProviderVerificationPage;